import { useEffect, useMemo, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { useContent } from "@/context/ContentContext";
import { PAGES, DEFAULTS } from "@/data/contentRegistry";
import ImageField from "@/components/admin/ImageField";
import { FloppyDisk, ArrowCounterClockwise, CaretDown, Eye, MagnifyingGlass } from "@phosphor-icons/react";

export default function AdminContent() {
  const { content, reload } = useContent();
  const [activeSlug, setActiveSlug] = useState(PAGES[0].slug);
  const [drafts, setDrafts] = useState({});
  const [savingKey, setSavingKey] = useState(null);
  const [query, setQuery] = useState("");
  const [openSections, setOpenSections] = useState({});

  // Sync server content into drafts (only for keys not currently edited)
  useEffect(() => {
    setDrafts((d) => {
      const next = { ...d };
      Object.entries(content || {}).forEach(([k, v]) => {
        if (next[k] === undefined) next[k] = v;
      });
      return next;
    });
  }, [content]);

  const activePage = PAGES.find((p) => p.slug === activeSlug);

  const getValue = (key) => drafts[key] ?? content?.[key] ?? DEFAULTS[key] ?? "";
  const setValue = (key, value) => setDrafts((d) => ({ ...d, [key]: value }));
  const isOverridden = (key) => {
    const v = content?.[key];
    return v !== undefined && v !== null && v !== "" && v !== DEFAULTS[key];
  };
  const isDirty = (key) => drafts[key] !== undefined && drafts[key] !== (content?.[key] ?? "");

  const save = async (key) => {
    const value = drafts[key] ?? "";
    setSavingKey(key);
    try {
      await api.put(`/content/${encodeURIComponent(key)}`, { value });
      await reload();
      toast.success("Enregistré");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingKey(null);
    }
  };

  const resetToDefault = async (key) => {
    setSavingKey(key);
    try {
      await api.delete(`/content/${encodeURIComponent(key)}`);
      setDrafts((d) => { const n = { ...d }; delete n[key]; return n; });
      await reload();
      toast.success("Réinitialisé à la valeur par défaut");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingKey(null);
    }
  };

  const saveAllInSection = async (section) => {
    const dirtyKeys = section.fields.filter((f) => isDirty(f.key)).map((f) => f.key);
    if (dirtyKeys.length === 0) { toast.info("Aucune modification à enregistrer"); return; }
    setSavingKey("__section__");
    try {
      for (const k of dirtyKeys) {
        await api.put(`/content/${encodeURIComponent(k)}`, { value: drafts[k] ?? "" });
      }
      await reload();
      toast.success(`${dirtyKeys.length} champ(s) enregistré(s)`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingKey(null);
    }
  };

  const previewUrl = useMemo(() => {
    const map = {
      "home": "/", "global": "/",
      "assurances-pro": "/assurances-professionnelles",
      "sante-prevoyance": "/sante-prevoyance",
      "patrimoine": "/patrimoine-placements",
      "vehicules-collection": "/vehicules-collection",
      "methode": "/methode",
      "partenaires": "/partenaires",
      "contact": "/contact",
    };
    return map[activeSlug] || "/";
  }, [activeSlug]);

  // Search across the active page
  const filteredSections = useMemo(() => {
    if (!activePage) return [];
    if (!query.trim()) return activePage.sections;
    const q = query.toLowerCase();
    return activePage.sections
      .map((s) => ({
        ...s,
        fields: s.fields.filter((f) =>
          f.label.toLowerCase().includes(q) ||
          f.key.toLowerCase().includes(q) ||
          String(getValue(f.key)).toLowerCase().includes(q),
        ),
      }))
      .filter((s) => s.fields.length > 0);
  }, [activePage, query, drafts, content]);

  const inputCls = "w-full bg-ducs-night border border-ducs-gold/25 px-3 py-2 text-ducs-cream rounded-sm text-sm focus:border-ducs-gold transition-colors";

  return (
    <div data-testid="admin-content" className="p-6 md:p-8">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <div className="label-caps mb-3">Contenus</div>
          <h1 className="font-serif text-3xl md:text-4xl text-ducs-cream">Éditeur de contenu</h1>
          <p className="text-ducs-mist mt-2 max-w-2xl text-sm">
            Modifiez tous les textes et images du site. Chaque modification est enregistrée
            individuellement. Si un champ n'est pas modifié, le texte d'origine reste affiché.
          </p>
        </div>
        <a
          href={previewUrl}
          target="_blank"
          rel="noreferrer"
          className="btn-secondary text-xs !py-2 !px-3"
          data-testid="admin-content-preview"
        >
          <Eye size={14} /> Aperçu de la page
        </a>
      </div>

      <div className="grid lg:grid-cols-12 gap-6">
        {/* Page list */}
        <aside className="lg:col-span-3">
          <div className="card-ducs hover:translate-y-0 p-2 sticky top-6">
            {PAGES.map((p) => (
              <button
                key={p.slug}
                onClick={() => { setActiveSlug(p.slug); setQuery(""); setOpenSections({}); }}
                data-testid={`content-page-${p.slug}`}
                className={`w-full text-left px-3 py-2.5 rounded-sm text-sm transition-colors flex items-center justify-between ${
                  activeSlug === p.slug ? "bg-ducs-gold/15 text-ducs-gold" : "text-ducs-cream/85 hover:bg-ducs-navy"
                }`}
              >
                <span>{p.label}</span>
                <span className="text-[10px] text-ducs-mist/70">
                  {p.sections.reduce((n, s) => n + s.fields.length, 0)}
                </span>
              </button>
            ))}
          </div>
        </aside>

        {/* Editor area */}
        <section className="lg:col-span-9 space-y-5">
          <div className="card-ducs hover:translate-y-0 p-5">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-serif text-2xl text-ducs-cream">{activePage?.label}</h2>
                {activePage?.description && (
                  <p className="text-sm text-ducs-mist mt-1">{activePage.description}</p>
                )}
              </div>
              <div className="relative">
                <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-ducs-mist" />
                <input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Rechercher un champ…"
                  className={`${inputCls} pl-9 min-w-[260px]`}
                  data-testid="content-search"
                />
              </div>
            </div>
          </div>

          {filteredSections.map((section) => {
            const isOpen = openSections[section.name] !== false; // open by default
            const dirtyCount = section.fields.filter((f) => isDirty(f.key)).length;
            return (
              <div key={section.name} className="card-ducs hover:translate-y-0">
                <button
                  type="button"
                  onClick={() => setOpenSections((o) => ({ ...o, [section.name]: !isOpen }))}
                  className="w-full flex items-center justify-between p-5 hover:bg-ducs-navy/30 transition-colors"
                >
                  <div className="text-left">
                    <h3 className="font-serif text-xl text-ducs-cream">{section.name}</h3>
                    <div className="text-xs text-ducs-mist mt-0.5">
                      {section.fields.length} champ(s)
                      {dirtyCount > 0 && <span className="text-ducs-gold"> · {dirtyCount} modifié(s)</span>}
                    </div>
                  </div>
                  <CaretDown size={18} className={`text-ducs-gold transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>

                {isOpen && (
                  <div className="border-t border-ducs-gold/10 p-5 space-y-5">
                    {section.fields.map((f) => {
                      const value = getValue(f.key);
                      const dirty = isDirty(f.key);
                      const overridden = isOverridden(f.key);
                      return (
                        <div key={f.key} className="border-b border-ducs-gold/5 last:border-0 pb-5 last:pb-0">
                          <div className="flex items-start justify-between gap-3 mb-2">
                            <div className="min-w-0">
                              <label className="text-sm text-ducs-cream font-medium block">
                                {f.label}
                                {dirty && <span className="ml-2 text-[10px] text-ducs-gold uppercase tracking-wider">non enregistré</span>}
                                {!dirty && overridden && <span className="ml-2 text-[10px] text-ducs-mist uppercase tracking-wider">personnalisé</span>}
                              </label>
                              <div className="text-[10px] text-ducs-mist/60 mt-0.5 font-mono break-all">{f.key}</div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              {overridden && (
                                <button
                                  type="button"
                                  onClick={() => resetToDefault(f.key)}
                                  disabled={savingKey === f.key}
                                  className="text-xs inline-flex items-center gap-1 text-ducs-mist hover:text-ducs-gold"
                                  title="Restaurer la valeur par défaut"
                                  data-testid={`reset-${f.key}`}
                                >
                                  <ArrowCounterClockwise size={12} /> Défaut
                                </button>
                              )}
                              <button
                                type="button"
                                onClick={() => save(f.key)}
                                disabled={!dirty || savingKey === f.key}
                                className="text-xs inline-flex items-center gap-1 px-2.5 py-1 bg-ducs-gold/15 text-ducs-gold rounded-sm disabled:opacity-40 hover:bg-ducs-gold/25"
                                data-testid={`save-${f.key}`}
                              >
                                <FloppyDisk size={12} /> Enregistrer
                              </button>
                            </div>
                          </div>

                          {f.type === "image" && (
                            <ImageField value={value} onChange={(v) => setValue(f.key, v)} />
                          )}
                          {f.type === "text" && (
                            <input
                              type="text"
                              value={value}
                              onChange={(e) => setValue(f.key, e.target.value)}
                              className={inputCls}
                              data-testid={`field-${f.key}`}
                            />
                          )}
                          {f.type === "textarea" && (
                            <textarea
                              rows={3}
                              value={value}
                              onChange={(e) => setValue(f.key, e.target.value)}
                              className={inputCls}
                              data-testid={`field-${f.key}`}
                            />
                          )}
                        </div>
                      );
                    })}

                    {dirtyCount > 1 && (
                      <div className="pt-3 border-t border-ducs-gold/10">
                        <button
                          type="button"
                          onClick={() => saveAllInSection(section)}
                          disabled={savingKey !== null}
                          className="btn-primary text-xs !py-2 !px-3"
                          data-testid={`save-section-${section.name}`}
                        >
                          <FloppyDisk size={14} /> Tout enregistrer dans cette section ({dirtyCount})
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}

          {filteredSections.length === 0 && (
            <div className="card-ducs p-10 text-center text-ducs-mist hover:translate-y-0">
              Aucun champ ne correspond à votre recherche.
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
